import { CultureE, CultureCodeE } from "./../../models/enums";
import { UserT } from "../../models/models";

const storageItemName = "tokens";

/** Cross-tab signal written when tokens or active user change */
export const AUTH_STORAGE_SYNC_KEY = "jumbomedia-auth-sync";

type TokenStorageEntryT = {
  user?: UserT;
  isLogged?: boolean;
  culture?: CultureCodeE;
};

/** Default token key used to persist selected language for non-logged (guest) users */
export const DEFAULT_GUEST_TOKEN = "0000-0000-0000-0000";

const isUserProfile = (value: unknown): value is UserT =>
  typeof value === "object" &&
  value !== null &&
  "userId" in value &&
  typeof (value as UserT).userId === "string";

/** Reads user profile from a storage entry, including legacy corrupted nesting. */
export const getUserFromTokenEntry = (
  entry: TokenStorageEntryT | null | undefined
): UserT | undefined => {
  if (!entry?.user) {
    return undefined;
  }

  if (isUserProfile(entry.user)) {
    return entry.user;
  }

  const nested = (entry.user as TokenStorageEntryT).user;
  if (isUserProfile(nested)) {
    return nested;
  }

  return undefined;
};

export const getCurrentUserProfile = (): UserT | undefined =>
  getUserFromTokenEntry(getLoggedUser()?.user);

export const notifyAuthStorageSync = () => {
  localStorage.setItem(AUTH_STORAGE_SYNC_KEY, String(Date.now()));
};

/** Fixes entries corrupted by older changeCurrentUserLangLocalStorage writes. */
export const repairTokenStorageIfNeeded = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (!existingTokensSrc) {
    return;
  }

  const tokens = JSON.parse(existingTokensSrc);
  let changed = false;

  Object.keys(tokens).forEach((tokenKey) => {
    const entry = tokens[tokenKey];
    const profile = getUserFromTokenEntry(entry);

    if (!entry || !profile) {
      return;
    }

    if (entry.user !== profile) {
      tokens[tokenKey] = {
        ...entry,
        user: profile,
      };
      changed = true;
    }
  });

  if (changed) {
    storage.setItem(storageItemName, JSON.stringify(tokens));
    notifyAuthStorageSync();
  }
};

/**
 * Removes the current user session. Returns true when the app should redirect to login.
 */
export const handleCurrentUserSessionExpired = (): boolean => {
  removeLoggedUserFromStorage();
  const remainingUsers = getAllUsersFromStorage();

  if (remainingUsers.length > 0) {
    markUserAsLoggedById(remainingUsers[0].userId);
    return false;
  }

  return true;
};

const getStorage = (): Storage => {
  const isRememberedMe = localStorage.getItem("rememberMe");
  if (isRememberedMe) {
    return localStorage;
  }

  // can't get access to the sibling tab with the same credentials while data stored  in session storage
  // return sessionStorage;
  return localStorage;
};

export const rememberUser = (id: string) => {
  const rememberMeSrc = localStorage.getItem("rememberMe");
  const rememberMe = rememberMeSrc ? JSON.parse(rememberMeSrc) : false;
  localStorage.setItem(
    "rememberMe",
    JSON.stringify({
      id,
      ...rememberMe,
    })
  );
};

export const writeTokenToStorage = (token: string) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);

    if (!(token in tokens)) {
      tokens[token] = null;
      storage.setItem(storageItemName, JSON.stringify(tokens));
    }
  } else {
    const emptyToken = { [token]: { isLogged: true } };

    storage.setItem(storageItemName, JSON.stringify(emptyToken));
  }
};

export const removeTokenFromStorage = (token: string) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);

    const leftTokens = {};

    Object.keys(tokens).forEach((key) => {
      if (key !== token) {
        leftTokens[key] = tokens[key];
      }
    });

    // Object.getOwnPropertyNames(tokens);
    if (Object.keys(leftTokens).length === 0) {
      storage.removeItem(storageItemName);
    } else {
      storage.setItem(storageItemName, JSON.stringify(leftTokens));
    }
  }
};

export const removeEmptyTokensFromStorage = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let userToken in tokens) {
      if (!tokens[userToken]) {
        delete tokens[userToken];
      }
    }
    storage.setItem(storageItemName, JSON.stringify(tokens));
  }
};

export const removeUserFromStorage = (userId: string) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let userToken in tokens) {
      if (getUserFromTokenEntry(tokens[userToken])?.userId === userId) {
        delete tokens[userToken];
      }
    }
    if (!Object.keys(tokens).length) {
      storage.removeItem(storageItemName);
    } else {
      storage.setItem(storageItemName, JSON.stringify(tokens));
    }
  }
};

export const bindUserDataToToken = (
  user: { user: UserT },
  token: string,
  markAsLogged: boolean
) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);

    tokens[token] = user;

    storage.setItem(storageItemName, JSON.stringify(tokens));
  }

  if (markAsLogged) {
    markUserAsLogged(token);
  }
};

export const markUserAsLogged = (token: string) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let userToken in tokens) {
      if (tokens?.[userToken]) {
        if (userToken === token) {
          tokens[userToken].isLogged = true;
        } else {
          tokens[userToken].isLogged = false;
        }
      }
    }
    storage.setItem(storageItemName, JSON.stringify(tokens));
  }
};

export const markUserAsLoggedById = (id: string) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let userToken in tokens) {
      if (tokens?.[userToken]) {
        if (getUserFromTokenEntry(tokens[userToken])?.userId === id) {
          tokens[userToken].isLogged = true;
        } else {
          tokens[userToken].isLogged = false;
        }
      }
    }
    storage.setItem(storageItemName, JSON.stringify(tokens));
  }
};

export const getLoggedUser = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    let loggedUser: any = {};
    for (let token in tokens) {
      if (tokens[token]?.isLogged) {
        loggedUser.token = token;
        loggedUser.user = tokens[token];
      }
    }

    return loggedUser;
  }
};

export const getAllUsersFromStorage = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    const users = Object.values(tokens);

    return users
      .map((entry: any) => getUserFromTokenEntry(entry))
      .filter((user): user is UserT => !!user);
  }

  return [];
};

export const removeLoggedUserFromStorage = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);

    if (Object.keys(tokens).length === 1) {
      // if the only one token exists, remove the whole field
      storage.removeItem(storageItemName);
    } else {
      for (let token in tokens) {
        if (tokens[token]?.isLogged) {
          removeTokenFromStorage(token);
        }
      }
    }
  }
};

export const getLoggedUserToken = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let token in tokens) {
      if (tokens[token]?.isLogged) {
        return token;
      }
    }
  }
};

export const updateTokens = ([accessToken, refreshToken]: [
  string,
  string | null | undefined
]) => {
  const user = getCurrentUserProfile();
  const tokenToRemove = getLoggedUserToken();

  if (!user || !tokenToRemove) {
    return;
  }

  writeTokenToStorage(accessToken);
  bindUserDataToToken(
    { user: { ...user, refreshToken: refreshToken ?? user.refreshToken } },
    accessToken,
    true
  );

  removeTokenFromStorage(tokenToRemove);
};

export const getTokenById = (id: number) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let token in tokens) {
      if (tokens[token]?.user.userId === id) {
        return token;
      }
    }
  }
};

export const getUserFromStorageById = (id: string) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  let foundUser;
  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);
    for (let token in tokens) {
      if (getUserFromTokenEntry(tokens[token])?.userId === id) {
        foundUser = tokens[token];
      }
    }
  }
  return foundUser;
};

export const getGuestLangFromStorage = (): CultureCodeE | null => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  if (!existingTokensSrc) return null;
  const tokens = JSON.parse(existingTokensSrc);
  const guest = tokens[DEFAULT_GUEST_TOKEN];
  if (!guest || !guest.culture) return null;
  return guest.culture === CultureCodeE.HE ? CultureCodeE.HE : CultureCodeE.EN;
};

export const setGuestLangInStorage = (culture: CultureCodeE) => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);
  const tokens = existingTokensSrc ? JSON.parse(existingTokensSrc) : {};
  tokens[DEFAULT_GUEST_TOKEN] = { culture };
  storage.setItem(storageItemName, JSON.stringify(tokens));
};

export const changeCurrentUserLangLocalStorage = (culture: CultureE) => {
  const profile = getCurrentUserProfile();
  const loggedUserToken = getLoggedUserToken();

  if (profile && loggedUserToken) {
    bindUserDataToToken(
      { user: { ...profile, culture } },
      loggedUserToken,
      true
    );
    notifyAuthStorageSync();
  } else {
    setGuestLangInStorage(
      (culture as unknown as string) === CultureCodeE.HE
        ? CultureCodeE.HE
        : CultureCodeE.EN
    );
  }
};

export const removeEmptyTokens = () => {
  const storage = getStorage();
  const existingTokensSrc = storage.getItem(storageItemName);

  if (existingTokensSrc) {
    const tokens = JSON.parse(existingTokensSrc);

    for (let token in tokens) {
      if (!tokens[token]) {
        delete tokens[token];
      }
    }
    storage.setItem(storageItemName, JSON.stringify(tokens));
  }
};
